/**
 * Catalogue Number validation + normalization.
 *
 * Mirrors `backend/services/catalogue_validation.py` exactly. If you change
 * one, change the other.
 *
 * Rules:
 *   • Max length: 20 chars (after normalization)
 *   • Letters: A-Z only (uppercase)
 *   • Digits: 0-9
 *   • Allowed special chars: & $ + - / . _ : ( ) !
 *   • First character must be: A-Z, 0-9, '-' or '/'
 *   • Other special chars are only allowed from position 2+
 *   • Auto-normalisation: upper-case, Æ→AE, Ø→OE, Å→AA, trim
 */

export const CATALOGUE_MAX_LENGTH = 20;

const ALLOWED_CHAR_RE = /^[A-Z0-9&$+\-/._:()!]+$/;
const ALLOWED_FIRST_CHAR_RE = /^[A-Z0-9\-/]$/;

/** Normalize a raw user-entered catalogue number. Returns null for empty input. */
export function normalizeCatalogueNumber(value) {
    if (value === null || value === undefined) return null;
    let s = String(value).trim();
    if (!s) return null;
    s = s
        .replace(/Æ/g, 'AE').replace(/æ/g, 'ae')
        .replace(/Ø/g, 'OE').replace(/ø/g, 'oe')
        .replace(/Å/g, 'AA').replace(/å/g, 'aa');
    return s.toUpperCase();
}

/**
 * Validate (and normalize) a catalogue number.
 *
 * Returns: { value: string|null, error: string|null }
 *   - Empty/null input: { value: null, error: null } — empty is allowed; callers
 *     enforce required-ness in their own context.
 *   - Valid: { value: normalized, error: null }
 *   - Invalid: { value: null, error: 'human-readable message' }
 */
export function validateCatalogueNumber(value) {
    const normalized = normalizeCatalogueNumber(value);
    if (normalized === null) return { value: null, error: null };

    if (normalized.length > CATALOGUE_MAX_LENGTH) {
        return { value: null, error: `Catalogue number may contain a maximum of ${CATALOGUE_MAX_LENGTH} characters.` };
    }
    if (!ALLOWED_CHAR_RE.test(normalized)) {
        return { value: null, error: 'Catalogue number contains invalid characters.' };
    }
    if (!ALLOWED_FIRST_CHAR_RE.test(normalized[0])) {
        return { value: null, error: "The first character must be a letter, number, '-' or '/'." };
    }
    return { value: normalized, error: null };
}
